import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { format } from "date-fns";
import { CalendarIcon, Search, Printer, FileSpreadsheet, ChevronDown, ChevronRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { exportToCsv } from "@/lib/exportCsv";
import { formatClassLabel } from "@/lib/classLabels";


import { getSignedFileUrl } from "@/lib/getSignedFileUrl";
import logoUrl from "@/assets/logo.png";

interface ModuleRef { module_code: string; module_name: string; class: string; semester: string; }

interface SessionRow {
  id: string;
  session_date: string;
  module_code: string | null;
  klass: string | null;
  topic: string | null;
  module_name?: string;
  semester?: string;
  scans: number;
}

interface RosterEntry {
  record_id: string; // attendance record id, or "absent-{studentId}" placeholder
  student_id: string;
  name: string;
  last_name: string;
  reg_no: string;
  signature_url: string | null;
  status: "present" | "absent";
}

const PAGE_SIZE = 1000;

async function fetchAllSessions(filters: { from?: Date; to?: Date }) {
  const all: any[] = [];
  let offset = 0;
  for (;;) {
    let q = supabase
      .from("attendance_sessions")
      .select("id, session_date, module_code, class, topic")
      .order("session_date", { ascending: false })
      .order("created_at", { ascending: false })
      .range(offset, offset + PAGE_SIZE - 1);
    if (filters.from) q = q.gte("session_date", format(filters.from, "yyyy-MM-dd"));
    if (filters.to) q = q.lte("session_date", format(filters.to, "yyyy-MM-dd"));
    const { data, error } = await q;
    if (error) throw error;
    const batch = (data ?? []) as any[];
    all.push(...batch);
    if (batch.length < PAGE_SIZE) break;
    offset += PAGE_SIZE;
  }
  return all;
}

async function fetchScanCounts(sessionIds: string[]) {
  const counts = new Map<string, number>();
  if (sessionIds.length === 0) return counts;
  // Count DISTINCT students per session so the badge matches the printable roster
  // (legacy data may contain duplicate scans before the unique index was added).
  const seen = new Map<string, Set<string>>();
  const idChunkSize = 200;
  for (let i = 0; i < sessionIds.length; i += idChunkSize) {
    const ids = sessionIds.slice(i, i + idChunkSize);
    let offset = 0;
    for (;;) {
      const { data, error } = await supabase
        .from("attendance_records")
        .select("session_id, student_id")
        .eq("attendance_type", "session")
        .eq("status", "present")
        .in("session_id", ids)
        .range(offset, offset + PAGE_SIZE - 1);
      if (error) throw error;
      const batch = (data ?? []) as { session_id: string; student_id: string }[];
      for (const r of batch) {
        if (!r.session_id || !r.student_id) continue;
        let set = seen.get(r.session_id);
        if (!set) { set = new Set(); seen.set(r.session_id, set); }
        set.add(r.student_id);
      }
      if (batch.length < PAGE_SIZE) break;
      offset += PAGE_SIZE;
    }
  }
  for (const [sid, set] of seen) counts.set(sid, set.size);
  return counts;
}

async function fetchRoster(
  sessionId: string,
  sessionClass: string | null,
  moduleCode?: string | null,
): Promise<RosterEntry[]> {
  // 1. Who was scanned for this session?
  const { data: records } = await supabase
    .from("attendance_records")
    .select("id, student_id, signature_url")
    .eq("session_id", sessionId)
    .eq("attendance_type", "session")
    .eq("status", "present");
  const recs = ((records ?? []) as { id: string; student_id: string; signature_url: string | null }[]);
  const presentMap = new Map(recs.map((r) => [r.student_id, r]));

  // Legacy sessions may have a NULL class — recover it from the module.
  if (!sessionClass && moduleCode) {
    const { data: mod } = await supabase
      .from("modules")
      .select("class")
      .eq("module_code", moduleCode)
      .maybeSingle();
    sessionClass = mod?.class ?? null;
  }

  // 2. Pull the entire active class roster so absent students appear too.
  let classStudents: any[] = [];
  if (sessionClass) {
    const { data } = await supabase
      .from("students")
      .select("id, first_name, last_name, student_number, signature_url")
      .eq("class", sessionClass)
      .in("status", ["active", "registered_active", "unregistered_active"]);
    classStudents = (data ?? []) as any[];
  }

  // Make sure scanned-but-not-in-class-roster students still show up.
  const knownIds = new Set(classStudents.map((s) => s.id));
  const orphanIds = recs.map((r) => r.student_id).filter((id) => id && !knownIds.has(id));
  if (orphanIds.length > 0) {
    const { data: extra } = await supabase
      .from("students")
      .select("id, first_name, last_name, student_number, signature_url")
      .in("id", orphanIds);
    classStudents = classStudents.concat((extra ?? []) as any[]);
  }

  const entries: RosterEntry[] = classStudents.map((s) => {
    const rec = presentMap.get(s.id);
    return {
      record_id: rec?.id ?? `absent-${s.id}`,
      student_id: s.id,
      name: `${s.first_name ?? ""} ${s.last_name ?? ""}`.trim() || "—",
      last_name: String(s.last_name ?? s.first_name ?? "").toLowerCase(),
      reg_no: s.student_number ?? "—",
      // Prefer the signature captured at scan time; fall back to the student's stored signature.
      signature_url: rec?.signature_url ?? s.signature_url ?? null,
      status: rec ? "present" : "absent",
    };
  });

  // Alphabetical A → Z by displayed name (first name first)
  return entries.sort((a, b) =>
    a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
  );
}

export function SessionAttendanceReport() {
  const [from, setFrom] = useState<Date | undefined>();
  const [to, setTo] = useState<Date | undefined>();
  const [semester, setSemester] = useState<string>("all");
  const [klass, setKlass] = useState<string>("all");
  const [moduleCode, setModuleCode] = useState<string>("all");
  const [search, setSearch] = useState("");
  const [allModules, setAllModules] = useState<ModuleRef[]>([]);
  const [sessions, setSessions] = useState<SessionRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [reloadTick, setReloadTick] = useState(0);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [rosterCache, setRosterCache] = useState<Record<string, RosterEntry[]>>({});
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkSessions, setBulkSessions] = useState<Array<{ session: SessionRow; roster: RosterEntry[] }>>([]);
  const [officerSig, setOfficerSig] = useState<string | null>(null);
  const [officerName, setOfficerName] = useState("");
  const printRef = useRef<HTMLDivElement>(null);

  // Load modules once
  useEffect(() => {
    supabase.from("modules").select("module_code, module_name, class, semester").then(({ data }) => {
      setAllModules((data ?? []) as ModuleRef[]);
    });
  }, []);

  // Load Academic Officer signature once for bulk-print sheet
  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from("system_settings")
        .select("key, value")
        .in("key", ["head_academic_signature", "academic_officer_signature_url"]);
      const settings = (data ?? []) as any[];
      const head = settings.find((s) => s.key === "head_academic_signature");
      const legacy = settings.find((s) => s.key === "academic_officer_signature_url");
      let sigUrl: string | null = null;
      let sigName = "";
      if (head?.value) {
        const v = head.value;
        sigName = v?.name ?? "";
        if (v?.file_path) sigUrl = await getSignedFileUrl(v.file_path);
        else if (typeof v === "string") sigUrl = v;
      }
      if (!sigUrl && legacy?.value) {
        const lv = legacy.value;
        sigUrl = typeof lv === "string" ? lv : lv?.url ?? null;
      }
      setOfficerSig(sigUrl);
      setOfficerName(sigName);
    })();
  }, []);

  const semesters = useMemo(() => [...new Set(allModules.map((m) => m.semester))].sort(), [allModules]);
  const classes = useMemo(() => {
    const ms = semester === "all" ? allModules : allModules.filter((m) => m.semester === semester);
    return [...new Set(ms.map((m) => m.class))].sort();
  }, [allModules, semester]);
  const modulesForFilter = useMemo(() => {
    return allModules.filter((m) =>
      (semester === "all" || m.semester === semester) &&
      (klass === "all" || m.class === klass)
    );
  }, [allModules, semester, klass]);

  useEffect(() => { setKlass("all"); setModuleCode("all"); }, [semester]);
  useEffect(() => { setModuleCode("all"); }, [klass]);

  // Load sessions + scan counts
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const rawSessions = await fetchAllSessions({ from, to });
        const ids = rawSessions.map((s) => s.id);
        const counts = await fetchScanCounts(ids);
        if (cancelled) return;

        const moduleByCode = new Map(allModules.map((m) => [m.module_code, m]));
        const decorated: SessionRow[] = rawSessions.map((s) => {
          const m = s.module_code ? moduleByCode.get(s.module_code) : undefined;
          return {
            id: s.id,
            session_date: s.session_date,
            module_code: s.module_code,
            klass: s.class ?? m?.class ?? null,
            topic: s.topic,
            module_name: m?.module_name,
            semester: m?.semester,
            scans: counts.get(s.id) ?? 0,
          };
        });

        // Reset roster cache so a session that gained scans reloads on expand
        setRosterCache({});
        setSessions(decorated);
      } catch (e: any) {
        console.error("Failed to load sessions:", e);
        toast.error("Failed to load sessions: " + (e?.message ?? "unknown error"));
        setSessions([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [from, to, allModules, reloadTick]);

  // Realtime: refresh when scans change
  useEffect(() => {
    let debounceId: number | undefined;
    const bump = () => {
      if (debounceId) window.clearTimeout(debounceId);
      debounceId = window.setTimeout(() => setReloadTick((t) => t + 1), 500);
    };
    const channel = supabase
      .channel("session-attendance-report-v2")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "attendance_records", filter: "attendance_type=eq.session" },
        bump
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "attendance_sessions" },
        bump
      )
      .subscribe();
    return () => {
      if (debounceId) window.clearTimeout(debounceId);
      supabase.removeChannel(channel);
    };
  }, []);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return sessions.filter((row) => {
      if (semester !== "all" && row.semester !== semester) return false;
      if (klass !== "all" && row.klass !== klass) return false;
      if (moduleCode !== "all" && row.module_code !== moduleCode) return false;
      if (s) {
        const hay = `${row.topic ?? ""} ${row.module_code ?? ""} ${row.module_name ?? ""}`.toLowerCase();
        if (!hay.includes(s)) return false;
      }
      return true;
    });
  }, [sessions, semester, klass, moduleCode, search]);

  const totalScans = useMemo(() => filtered.reduce((acc, r) => acc + r.scans, 0), [filtered]);

  async function ensureRoster(sessionId: string, sessionClass: string | null, moduleCode?: string | null): Promise<RosterEntry[]> {
    if (rosterCache[sessionId]) return rosterCache[sessionId];
    const roster = await fetchRoster(sessionId, sessionClass, moduleCode);
    setRosterCache((prev) => ({ ...prev, [sessionId]: roster }));
    return roster;
  }

  async function toggleExpand(row: SessionRow) {
    if (expanded === row.id) { setExpanded(null); return; }
    setExpanded(row.id);
    if (!rosterCache[row.id]) await ensureRoster(row.id, row.klass, row.module_code);
  }

  async function exportSessionCsv(row: SessionRow) {
    const roster = await ensureRoster(row.id, row.klass, row.module_code);
    const headers = ["SN", "Student Name", "NACTEVET No", "Status", "Signature Captured"];
    const data = roster.map((r, i) => [
      String(i + 1),
      r.name,
      r.reg_no,
      r.status === "present" ? "Present" : "Absent",
      r.signature_url ? "Yes" : "",
    ]);
    const safe = (row.module_code ?? "session").replace(/[^a-zA-Z0-9_-]/g, "_");
    const stamp = format(new Date(row.session_date), "yyyyMMdd");
    exportToCsv(`session-${safe}-${stamp}.csv`, headers, data);
  }

  async function exportBulkPdf() {
    if (filtered.length === 0) return;
    setBulkLoading(true);
    try {
      const loaded = await Promise.all(
        filtered.map(async (s) => ({ session: s, roster: await ensureRoster(s.id, s.klass, s.module_code) }))
      );
      setBulkSessions(loaded);
      // Wait for DOM
      setTimeout(() => {
        window.print();
        // Clear after print to free memory
        setTimeout(() => setBulkSessions([]), 1000);
      }, 300);
    } catch (e: any) {
      toast.error("Failed to prepare bulk PDF: " + (e?.message ?? "unknown error"));
    } finally {
      setBulkLoading(false);
    }
  }

  const [singlePdfLoadingId, setSinglePdfLoadingId] = useState<string | null>(null);

  async function exportSessionPdf(row: SessionRow) {
    setSinglePdfLoadingId(row.id);
    try {
      const roster = await ensureRoster(row.id, row.klass, row.module_code);
      setBulkSessions([{ session: row, roster }]);
      setTimeout(() => {
        window.print();
        setTimeout(() => setBulkSessions([]), 1000);
      }, 300);
    } catch (e: any) {
      toast.error("Failed to prepare PDF: " + (e?.message ?? "unknown error"));
    } finally {
      setSinglePdfLoadingId(null);
    }
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="pt-6 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">From</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className={cn("w-full justify-start text-left font-normal", !from && "text-muted-foreground")}>
                    <CalendarIcon className="mr-2 h-4 w-4" />{from ? format(from, "PP") : "Any"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar mode="single" selected={from} onSelect={setFrom} className={cn("p-3 pointer-events-auto")} />
                </PopoverContent>
              </Popover>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">To</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className={cn("w-full justify-start text-left font-normal", !to && "text-muted-foreground")}>
                    <CalendarIcon className="mr-2 h-4 w-4" />{to ? format(to, "PP") : "Any"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar mode="single" selected={to} onSelect={setTo} className={cn("p-3 pointer-events-auto")} />
                </PopoverContent>
              </Popover>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Semester</Label>
              <Select value={semester} onValueChange={setSemester}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  {semesters.map((s) => <SelectItem key={s} value={s}>Semester {s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Class</Label>
              <Select value={klass} onValueChange={setKlass}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  {classes.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Module</Label>
              <Select value={moduleCode} onValueChange={setModuleCode}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  {modulesForFilter.map((m) => (
                    <SelectItem key={m.module_code} value={m.module_code}>{m.module_code}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Search session/topic</Label>
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input className="pl-8" placeholder="Topic or module" value={search} onChange={(e) => setSearch(e.target.value)} />
              </div>
            </div>
          </div>
          <div className="flex justify-between items-center gap-2 flex-wrap">
            <p className="text-sm text-muted-foreground">
              {loading ? "Loading…" : `${filtered.length} session(s) • ${totalScans} scan(s)`}
            </p>
            <Button size="sm" variant="outline" onClick={exportBulkPdf} disabled={filtered.length === 0 || bulkLoading}>
              {bulkLoading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Printer className="w-4 h-4 mr-1" />}
              Bulk Export PDF
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-8" />
              <TableHead>Date</TableHead>
              <TableHead>Class</TableHead>
              <TableHead>Module</TableHead>
              <TableHead>Session Topic</TableHead>
              <TableHead className="text-right">Scans</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">{loading ? "Loading…" : "No sessions."}</TableCell></TableRow>
            ) : filtered.map((row) => (
              <Fragment key={row.id}>
                <TableRow>
                  <TableCell>
                    <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => toggleExpand(row)}>
                      {expanded === row.id ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    </Button>
                  </TableCell>
                  <TableCell>{format(new Date(row.session_date), "PP")}</TableCell>
                  <TableCell>{row.klass ?? "—"}</TableCell>
                  <TableCell className="font-mono text-xs">
                    <div>{row.module_code ?? "—"}</div>
                    {row.module_name && <div className="text-muted-foreground text-[10px] font-sans">{row.module_name}</div>}
                  </TableCell>
                  <TableCell className="max-w-[280px]">
                    <span title={row.topic ?? ""}>{row.topic ?? "—"}</span>
                  </TableCell>
                  <TableCell className="text-right">
                    <Badge variant={row.scans > 0 ? "default" : "secondary"}>{row.scans}</Badge>
                  </TableCell>
                  <TableCell className="text-right space-x-1 print:hidden">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => exportSessionPdf(row)}
                      disabled={singlePdfLoadingId === row.id || bulkLoading}
                      title="Export this session's attendance sheet as PDF"
                    >
                      {singlePdfLoadingId === row.id
                        ? <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                        : <Printer className="w-4 h-4 mr-1" />}
                      Export PDF
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => exportSessionCsv(row)}>
                      <FileSpreadsheet className="w-4 h-4 mr-1" /> CSV
                    </Button>
                  </TableCell>
                </TableRow>
                {expanded === row.id && (
                  <TableRow key={row.id + "-expanded"}>
                    <TableCell colSpan={7} className="bg-muted/30 p-3">
                      <div className="text-xs text-muted-foreground mb-2">{formatClassLabel(row.klass)}</div>
                      {!rosterCache[row.id] ? (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Loader2 className="w-3 h-3 animate-spin" /> Loading roster…
                        </div>
                      ) : rosterCache[row.id].length === 0 ? (
                        <p className="text-sm text-muted-foreground">No active students in this class.</p>
                      ) : (
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="w-10">SN</TableHead>
                              <TableHead>Student Name</TableHead>
                              <TableHead>NACTEVET No.</TableHead>
                              <TableHead className="w-24">Status</TableHead>
                              <TableHead>Signature</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {rosterCache[row.id].map((r, i) => (
                              <TableRow key={r.record_id}>
                                <TableCell>{i + 1}</TableCell>
                                <TableCell className="font-medium">{r.name}</TableCell>
                                <TableCell className="font-mono text-xs">{r.reg_no}</TableCell>
                                <TableCell>
                                  {r.status === "present" ? (
                                    <Badge variant="default">Present</Badge>
                                  ) : (
                                    <Badge variant="outline" className="text-muted-foreground">Absent</Badge>
                                  )}
                                </TableCell>
                                <TableCell>
                                  {r.signature_url ? (
                                    <img src={r.signature_url} alt="signature" className="h-8 max-w-[100px] object-contain" />
                                  ) : (
                                    <span className="text-muted-foreground">—</span>
                                  )}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      )}
                    </TableCell>
                  </TableRow>
                )}
              </Fragment>
            ))}
          </TableBody>
        </Table>
      </Card>

      {/* Hidden bulk-print sheet — one A4 page per session */}
      {bulkSessions.length > 0 && (
        <div ref={printRef} className="hidden print:block print-area">
          <style>{`
            @media print {
              @page { size: A4 landscape; margin: 12mm; }
              body * { visibility: hidden; }
              .print-area, .print-area * { visibility: visible; }
              .print-area { position: absolute; left: 0; top: 0; width: 100%; }
              .print-area table { width: 100%; border-collapse: collapse; font-size: 9pt; }
              .print-area th, .print-area td { border: 1px solid #333; padding: 2px 5px; text-align: left; vertical-align: middle; }
              .print-area thead { background: #f3f4f6; }
              .print-area .session-page { page-break-after: always; page-break-inside: avoid; break-inside: avoid; }
              .print-area .session-page:last-child { page-break-after: auto; }
              .print-area .print-header { border-bottom: 2px solid #e3833a; margin-bottom: 12px; padding-bottom: 8px; }
              .print-area .print-meta { margin: 8px 0 12px; font-size: 10pt; }
              .print-area .print-meta div { padding: 2px 0; }
              .print-area .print-meta .label { display: inline-block; min-width: 90px; color: #555; font-weight: 600; }
            }
          `}</style>
          {bulkSessions.map(({ session, roster }) => {
            const m = allModules.find((x) => x.module_code === session.module_code);
            const ROWS_PER_PAGE = 25;
            const pages: RosterEntry[][] = roster.length === 0
              ? [[]]
              : Array.from({ length: Math.ceil(roster.length / ROWS_PER_PAGE) }, (_, i) =>
                  roster.slice(i * ROWS_PER_PAGE, (i + 1) * ROWS_PER_PAGE),
                );
            return (
              <Fragment key={session.id}>
                {pages.map((pageRoster, pageIdx) => {
                  const isLastPage = pageIdx === pages.length - 1;
                  return (
                    <div key={session.id + "-p" + pageIdx} className="session-page">
                      <div className="print-header" style={{ display: "flex", alignItems: "center", gap: 16 }}>
                        <img src={logoUrl} alt="St. David College" style={{ height: 56 }} />
                        <div>
                          <div style={{ fontSize: "14pt", fontWeight: 700 }}>ST. DAVID COLLEGE OF HEALTH SCIENCES</div>
                          <div style={{ fontSize: "11pt", fontWeight: 600, color: "#e3833a" }}>SESSION ATTENDANCE SHEET</div>
                        </div>
                      </div>
                      <div className="print-meta">
                        <div><span className="label">Class:</span> <span>{formatClassLabel(session.klass)}</span></div>
                        <div><span className="label">Module:</span> <span>{(session.module_code ?? "—") + (m?.module_name ? " — " + m.module_name : "")}</span></div>
                        <div><span className="label">Session:</span> <span>{session.topic || "Untitled session"}</span></div>
                        <div><span className="label">Page:</span> <span>{pageIdx + 1} of {pages.length} · {roster.length} student{roster.length === 1 ? "" : "s"}</span></div>
                      </div>
                      <table>
                        <thead>
                          <tr>
                            <th style={{ width: "5%" }}>SN</th>
                            <th style={{ width: "35%" }}>Student Name</th>
                            <th style={{ width: "20%" }}>NACTEVET No.</th>
                            <th style={{ width: "15%" }}>Status</th>
                            <th style={{ width: "25%" }}>Signature</th>
                          </tr>
                        </thead>
                        <tbody>
                          {pageRoster.length === 0 ? (
                            <tr><td colSpan={5} style={{ textAlign: "center", padding: 16, color: "#777" }}>No active students in this class.</td></tr>
                          ) : pageRoster.map((r, i) => (
                            <tr key={r.record_id}>
                              <td>{pageIdx * ROWS_PER_PAGE + i + 1}</td>
                              <td>{r.name}</td>
                              <td style={{ fontFamily: "monospace" }}>{r.reg_no}</td>
                              <td>{r.status === "present" ? "Present" : "Absent"}</td>
                              <td>{r.signature_url
                                ? <img src={r.signature_url} alt="sig" style={{ height: 28, maxWidth: 120, objectFit: "contain" }} />
                                : ""}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {isLastPage && (
                        <div style={{ marginTop: 24, paddingTop: 12, borderTop: "1px solid #999" }}>
                          <div style={{ fontSize: "10pt", fontWeight: 600, marginBottom: 8 }}>Certified by Academic Officer</div>
                          <div style={{ display: "flex", alignItems: "flex-end", gap: 32, marginTop: 8 }}>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: "9pt", color: "#555", marginBottom: 4 }}>Signature</div>
                              {officerSig
                                ? <img src={officerSig} alt="Academic Officer signature" style={{ height: 50, maxWidth: 200, objectFit: "contain" }} />
                                : <div style={{ borderBottom: "1px solid #333", height: 50 }} />}
                            </div>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: "9pt", color: "#555", marginBottom: 4 }}>Name</div>
                              <div style={{ borderBottom: "1px solid #333", height: 24, fontSize: "10pt", paddingBottom: 2 }}>{officerName}</div>
                            </div>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: "9pt", color: "#555", marginBottom: 4 }}>Date</div>
                              <div style={{ borderBottom: "1px solid #333", height: 24 }} />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </Fragment>
            );
          })}
        </div>
      )}
    </div>
  );
}
